
import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import CompareHeader from '@/components/compare/CompareHeader';
import CompareStatus from '@/components/compare/CompareStatus';
import CompareContent from '@/components/compare/CompareContent';
import { useCompareTeams } from '@/hooks/useCompareTeams';
import { useToast } from '@/hooks/use-toast';

interface ComparePageState {
  homeTeam: string;
  awayTeam: string;
  league: string;
  year: string;
}

const CompareContainer = () => {
  const location = useLocation();
  const { toast } = useToast();
  const state = location.state as ComparePageState;

  // Redirect if no state is present
  if (!state) {
    toast({
      title: "Missing Information",
      description: "Please select teams to compare from the team details page",
      variant: "destructive"
    });
    return <Navigate to="/" replace />;
  }

  const { homeTeam, awayTeam } = state;
  const [selectedSeasons, setSelectedSeasons] = useState<string[]>([state.year]);
  
  const handleSeasonsChange = (newSeasons: string[]) => {
    // Already sorted in CompareHeader
    setSelectedSeasons(newSeasons);
  };
  
  const {
    data: matchData,
    isLoading,
    error
  } = useCompareTeams({
    homeTeam,
    awayTeam,
    years: selectedSeasons
  });

  // Show loading or error states
  if (isLoading || error) {
    return <CompareStatus isLoading={isLoading} error={error} />;
  }

  // Check if we have valid match data
  const hasValidMatchData = matchData && matchData.homeTeamMatches && matchData.awayTeamMatches && (matchData.homeTeamMatches.length > 0 || matchData.awayTeamMatches.length > 0);
  
  if (!hasValidMatchData) {
    toast({
      title: "No Match Data",
      description: `No matches found for ${homeTeam} vs ${awayTeam} in the selected seasons.`,
      variant: "destructive"
    });
    return <Navigate to="/" replace />;
  }
  
  return (
    <>
      <CompareHeader 
        selectedSeasons={selectedSeasons} 
        onSeasonsChange={handleSeasonsChange} 
        homeTeam={homeTeam} 
        awayTeam={awayTeam} 
      />
      
      <CompareContent 
        homeTeam={homeTeam} 
        awayTeam={awayTeam} 
        matchData={matchData} 
      />
    </>
  );
};

export default CompareContainer;
